import { Button, Intent, NonIdealState } from "@blueprintjs/core";
import { FieldSet, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import { useHistory } from "react-router-dom";

import { CompetitionImportDto, CompetitionResultApi, ErrorDetails, ESRSHExceptions } from "../../../../api";
import { useDialog } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { CompetitionResultImportDataTableForm } from "./CompetitionResultImportDataTableForm";

export interface ICompetitionResultImportPanelProps {
  competitionId: number;
}

export const CompetitionResultImportPanel: React.FunctionComponent<ICompetitionResultImportPanelProps> = ({
  competitionId,
}) => {
  const api = useApiService(CompetitionResultApi);

  const { t } = useTl();
  const { showDialogPromise } = useDialog();
  const history = useHistory();

  const [competitionResultsImported, setCompetitionResultsImported] = React.useState<CompetitionImportDto[]>();
  const [files, setFiles] = React.useState<any[]>();
  const [importing, setimporting] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const importCallback = React.useCallback(async () => {
    setimporting(true);
    try {
      const importedResults = await api.importCompetitionResultsFromExcel(competitionId, {
        data: files[0].file,
        fileName: files[0].filename,
      });
      setCompetitionResultsImported(importedResults);
      setimporting(false);
    } catch {
      showError(t(ETLCodes.Error));
      setimporting(false);
    }
  }, [api, competitionId, files, t]);

  const saveImport = React.useCallback(
    async (data: CompetitionImportDto[], shouldDouble: boolean, expoId: number) => {
      setSaving(true);
      try {
        await showDialogPromise({
          message: t(ETLCodes.ImportWillStartYouWillReceiveAnEmailWhenFinished),
          title: t(ETLCodes.Warning),
          buttons: [{ resultType: "ok", text: t(ETLCodes.Ok) }],
        });
        await api.saveImportedDtos(shouldDouble, expoId, data);
        showSuccess(t(ETLCodes.ImportStarted));
        setSaving(false);
        history.push(`${ERoutes.competition}/${competitionId}/detail/edit`);
      } catch (e) {
        const details: ErrorDetails = JSON.parse(e.response);
        if (details?.errorCode) {
          if (details?.errorCode === ESRSHExceptions.CompetitionImportInProgress) {
            showError(t(ETLCodes.ImportAlreadyInProgressPleaseWait));
          }
        } else {
          showError(t(ETLCodes.Error));
        }
        setSaving(false);
      }
    },
    [api, competitionId, history, showDialogPromise, t]
  );

  const removeFile = React.useCallback(() => {
    setFiles([]);
    setCompetitionResultsImported(null);
  }, []);

  return (
    <>
      <FilePond
        files={files}
        allowMultiple={false}
        onupdatefiles={setFiles}
        labelIdle={t(ETLCodes.FilePondDragOrSelect)}
        onremovefile={removeFile}
      />
      <Button
        onClick={importCallback}
        loading={importing}
        text={t(ETLCodes.ReadExcelFile)}
        disabled={!files || files.length === 0}
        minimal={false}
        intent={Intent.PRIMARY}
        icon={<i className="far fa-file-excel" />}
      />

      {competitionResultsImported && files?.length > 0 ? (
        <FieldSet
          title={t(ETLCodes.TableResults_Plural, {
            count: competitionResultsImported?.length.toLocaleString("be-BE"),
          })}
        >
          <CompetitionResultImportDataTableForm
            competitionId={competitionId}
            initialData={competitionResultsImported}
            onSave={saveImport}
            saving={saving}
            importFunc={importCallback}
          />
        </FieldSet>
      ) : (
        <NonIdealState icon="resolve" description={t(ETLCodes.PleaseImportFile)} />
      )}
    </>
  );
};
